// frontend/CostaBuyBoxDashboardPage.tsx
import React, {useState, useEffect, FC} from 'react'
import axios from 'axios'
import {PageTitle} from '../../../../../_designereyes/layout/core'
import {API_URL} from '../../../../modules/auth/core/_requests'
import {DataTable, DataTableFilterMeta} from 'primereact/datatable'
import {Column} from 'primereact/column'
import {Chart} from 'primereact/chart'
import '../BuyBoxDashboardPage.css'

interface BuyBoxDataRaw {
  marketplace: string
  sku: string
  asin: string
  title: string
  brand: string
  buy_box_winner: string
  buy_box_price: number
  my_price: number
  quantity: number
}

interface BuyBoxData extends BuyBoxDataRaw {
  priceDiff: number
  priceDiffPct: number
}

export const CostaBuyBoxDashboardPage: FC = () => {
  const [data, setData] = useState<BuyBoxData[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    asin: {operator: 'and', constraints: [{value: null, matchMode: 'contains'}]},
    title: {operator: 'and', constraints: [{value: null, matchMode: 'contains'}]},
    buy_box_winner: {operator: 'and', constraints: [{value: null, matchMode: 'contains'}]},
    buy_box_price: {operator: 'and', constraints: [{value: null, matchMode: 'equals'}]},
    my_price: {operator: 'and', constraints: [{value: null, matchMode: 'equals'}]},
    priceDiff: {operator: 'and', constraints: [{value: null, matchMode: 'equals'}]},
    quantity: {operator: 'and', constraints: [{value: 0, matchMode: 'gt'}]},
    global: {value: '', matchMode: 'contains'},
  })

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const resp = await axios.get(`${API_URL}/api/costa-buybox`)
      const raw: BuyBoxDataRaw[] = resp.data
      const mapped = raw.map((row) => {
        const priceDiff = row.my_price - row.buy_box_price
        let priceDiffPct = 0
        if (row.buy_box_price > 0) {
          priceDiffPct = (priceDiff / row.buy_box_price) * 100
        }
        return {
          ...row,
          priceDiff,
          priceDiffPct,
        }
      })
      setData(mapped)
    } catch (err) {
      console.error('Error fetching buybox data:', err)
    } finally {
      setLoading(false)
    }
  }

  const analyticsData = data.filter(
    (d) => d.marketplace === 'AUS' && d.quantity > 0 && d.buy_box_price > 0
  )
  const winCount = analyticsData.filter((d) => d.buy_box_winner === 'EC AMZ').length
  const loseCount = analyticsData.length - winCount
  const buyBoxPieData = {
    labels: ['Win BB', 'Lose BB'],
    datasets: [
      {
        data: [winCount, loseCount],
        backgroundColor: ['#22c55e', '#ef4444'],
      },
    ],
  }
  const priceMatchedNotWinningCount = analyticsData.filter(
    (d) => d.my_price === d.buy_box_price && d.buy_box_winner !== 'EC AMZ'
  ).length
  const rowClassName = (rowData: BuyBoxData) => {
    return rowData.buy_box_winner === 'EC AMZ' ? '' : 'not-owning-buybox'
  }
  const asinBodyTemplate = (rowData: BuyBoxData) => {
    if (!rowData.asin) return ''
    const link = `https://www.amazon.com/dp/${rowData.asin}`
    return (
      <a href={link} target='_blank' rel='noopener noreferrer'>
        {rowData.asin}
      </a>
    )
  }
  const priceDiffBodyTemplate = (rowData: BuyBoxData) => {
    if (rowData.priceDiff === 0) return '-'
    const diffFixed = rowData.priceDiff.toFixed(2)
    const pctFixed = rowData.priceDiffPct.toFixed(2)
    return `${diffFixed}(${pctFixed}%)`
  }
  const bbOwnedBodyTemplate = (rowData: BuyBoxData) => {
    return rowData.buy_box_winner === 'EC AMZ' ? 'Yes' : 'No'
  }
  const onFilterChange = (e: any) => {
    setFilters(e.filters)
  }

  return (
    <>
      <PageTitle>Buy Box Dashboard</PageTitle>
      <div className='row mb-3'>
        <div className='col-12 col-md-4 mb-3 mb-md-0'>
          <div className='card d-flex align-items-center justify-content-center p-3 h-100'>
            <h5 className='mb-3'>Buy Box Ownership</h5>
            <Chart type='pie' data={buyBoxPieData} style={{width: '12rem'}} />
            <p className='mt-2 mb-0 text-center'>
              {winCount} / {analyticsData.length} <span style={{opacity: 0.8}}>won</span>
            </p>
          </div>
        </div>
        <div className='col-12 col-md-4 mb-3 mb-md-0'>
          <div className='card d-flex align-items-center justify-content-center p-3 h-100'>
            <h5 className='mb-2'>Price-Matched but Not Winning</h5>
            <span className='display-6'>{priceMatchedNotWinningCount}</span>
          </div>
        </div>
        <div className='col-12 col-md-4'>
          <div className='card d-flex align-items-center justify-content-center p-3 h-100'>
            <h5 className='mb-2'>Want More Analytics?</h5>
            <p className='mb-0 text-center'>Choose what you want to see here</p>
          </div>
        </div>
      </div>
      <div className='card p-3 text-light buybox-dashboard'>
        <div className='mb-3'>
          <h2 className='mb-0'>Buy Box Dashboard</h2>
          <p className='text-muted'>Zero-inventory items are hidden by default.</p>
        </div>
        <DataTable
          value={data}
          paginator
          rows={200}
          rowsPerPageOptions={[50, 100, 200, 500]}
          loading={loading}
          filters={filters}
          filterDisplay='menu'
          onFilter={onFilterChange}
          sortMode='single'
          scrollable
          scrollHeight='600px'
          className='p-datatable-sm'
          rowClassName={rowClassName}
          dataKey='sku'
        >
          <Column
            field='asin'
            header='ASIN'
            dataType='text'
            sortable
            filter
            filterPlaceholder='Contains...'
            body={asinBodyTemplate}
            style={{minWidth: '120px'}}
          />
          <Column
            field='title'
            header='Title'
            dataType='text'
            sortable
            filter
            filterPlaceholder='Contains...'
            style={{minWidth: '200px'}}
          />
          <Column
            field='buy_box_winner'
            header='Buy Box Winner'
            dataType='text'
            sortable
            filter
            filterPlaceholder='Contains...'
            style={{minWidth: '130px'}}
          />
          <Column
            field='buy_box_price'
            header='Buy Box Price'
            dataType='numeric'
            sortable
            filter
            filterPlaceholder='Compare...'
            style={{minWidth: '130px'}}
          />
          <Column
            field='my_price'
            header='My Price'
            dataType='numeric'
            sortable
            filter
            filterPlaceholder='Compare...'
            style={{minWidth: '130px'}}
          />
          <Column
            field='priceDiff'
            header='Price Diff'
            dataType='numeric'
            sortable
            filter
            filterPlaceholder='Compare...'
            style={{minWidth: '130px'}}
            body={priceDiffBodyTemplate}
          />
          <Column
            field='quantity'
            header='Quantity'
            dataType='numeric'
            sortable
            filter
            filterPlaceholder='Compare...'
            style={{minWidth: '100px'}}
          />
          <Column
            header='BB Owned'
            dataType='text'
            sortable
            style={{minWidth: '100px'}}
            body={bbOwnedBodyTemplate}
          />
        </DataTable>
      </div>
    </>
  )
}

export const CostaBuyBoxDashboardWrapper: FC = () => {
  return <CostaBuyBoxDashboardPage />
}
